import { useState, useEffect, useCallback } from 'react';
import { brandService } from '../services/brandService';

const STORAGE_KEY = 'lastSeenDiscoverDate';

/**
 * Hook that checks if there are new stores the user hasn't seen yet.
 *
 * Compares the most recent brand's `createdAt` against a date stored in
 * localStorage. Returns `hasNewStores` (for rendering a badge) and
 * `markAsSeen` (to dismiss the badge when the user visits Discover).
 */
export function useDiscoverNotification() {
    const [hasNewStores, setHasNewStores] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const check = async () => {
            try {
                const brands = await brandService.getAll();
                if (cancelled || brands.length === 0) return;

                // Find the most recent createdAt among all brands
                const latestDate = brands.reduce<string | undefined>(
                    (max, b) => {
                        if (!b.createdAt) return max;
                        if (!max) return b.createdAt;
                        return b.createdAt > max ? b.createdAt : max;
                    },
                    undefined,
                );

                if (!latestDate) return;

                const lastSeen = localStorage.getItem(STORAGE_KEY);
                if (!lastSeen || latestDate > lastSeen) {
                    setHasNewStores(true);
                }
            } catch {
                // Silently ignore — badge just won't show
            }
        };

        check();
        return () => { cancelled = true; };
    }, []);

    /** Call this when the user opens the Discover tab to dismiss the badge. */
    const markAsSeen = useCallback(() => {
        localStorage.setItem(STORAGE_KEY, new Date().toISOString());
        setHasNewStores(false);
    }, []);

    return { hasNewStores, markAsSeen } as const;
}
